import { createContext, useContext, ReactNode } from 'react';

export interface SSRData {
  data: Record<string, unknown>;
  seo: {
    title: string;
    description: string;
    ogImage?: string;
    ogUrl?: string;
  };
}

const SSRContext = createContext<SSRData | null>(null);

interface SSRProviderProps {
  value: SSRData | null;
  children: ReactNode;
}

export function SSRProvider({ value, children }: SSRProviderProps) {
  return <SSRContext.Provider value={value}>{children}</SSRContext.Provider>;
}

/**
 * Hook to access server-side loaded data.
 * Returns null on client-side navigations (pages should fall back to useEffect fetching).
 */
export function useSSRData(): SSRData | null {
  return useContext(SSRContext);
}

/**
 * Get SSR data from window.__SSR_DATA__ on the client (initial page load only).
 * Returns null if not available.
 */
export function getClientSSRData(): SSRData | null {
  if (typeof window !== 'undefined' && (window as any).__SSR_DATA__) {
    const data = (window as any).__SSR_DATA__ as SSRData;
    // Clear after first read to avoid stale data on SPA navigations
    delete (window as any).__SSR_DATA__;
    return data;
  }
  return null;
}
